import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { firstValueFrom } from 'rxjs';

/** OGC API Processes base URL (e.g. `https://v2.dev.ogc-s3.iudx.io/`). */
export const OGC_API_BASE_URL = new InjectionToken<string>('OGC_API_BASE_URL');

/** OGC process execution inputs shared across S3 / onboarding processes. */
export interface OgcExecutionConfig {
  bucketName: string;
  region: string;
  fileType: string;
  version: string;
  s3BucketIdentifier: string;
}

export const OGC_EXECUTION_CONFIG = new InjectionToken<OgcExecutionConfig>(
  'OGC_EXECUTION_CONFIG',
);

export interface OgcProcessLink {
  type?: string;
  rel?: string;
  title?: string;
  href?: string;
}

export interface OgcProcessSummary {
  version?: string;
  id: string;
  title: string;
  description?: string;
  jobControlOptions?: string[];
  keywords?: string[];
  outputTransmission?: string[];
  links?: OgcProcessLink[];
}

export interface OgcProcessesListResponse {
  processes: OgcProcessSummary[];
  links?: OgcProcessLink[];
}

export interface OgcPresignedUrlOutputs {
  status?: string;
  message?: string;
  S3PreSignedUrl?: string;
  s3ObjectKeyName?: string;
}

export interface OgcInitiateMultipartOutputs {
  uploadId?: string;
  chunkSize?: number;
  totalParts?: number;
  presignedUrls?: Array<{ url: string; partNumber: number }>;
  /** Server-side object key when the process renames/prefixes the upload. */
  s3KeyName?: string;
}

export interface OgcCompleteMultipartOutputs {
  message?: string;
  location?: string;
  status?: string;
}

export interface OgcCollectionOnboardingOutputs {
  type?: string;
  jobId?: string;
  status?: string;
  processId?: string;
}

export interface StacCollectionPayload {
  type: 'Collection';
  id: string;
  title: string;
  description: string;
  license: string;
  extent: {
    spatial: {
      bbox: number[][];
      crs: string;
    };
    temporal: {
      interval: string[][];
    };
  };
  datetime_key: 'datetime';
}

export type StacCollectionResponse = Record<string, unknown>;

export interface StacItemPayload {
  id: string;
  type: 'Feature';
  bbox: number[];
  geometry: {
    type: string;
    coordinates: unknown;
  };
  properties: Record<string, unknown>;
  assets?: Record<string, StacAssetPayload>;
}

export interface StacAssetPayload {
  href: string;
  title: string;
  description: string;
  type: string;
  roles: string[];
  size?: number;
  s3BucketId?: string;
}

export interface StacItemCollectionResponse {
  type?: string;
  features?: StacItemPayload[];
  items?: StacItemPayload[];
  numberMatched?: number;
  numberReturned?: number;
  [key: string]: unknown;
}

export interface OgcConfiguredS3Bucket {
  s3BucketIdentifier?: string;
  bucketName?: string;
  readAccess?: string;
  [key: string]: unknown;
}

/** A link entry in an OGC API Features collection description (`GET /collections/{id}`). */
export interface OgcCollectionLink {
  href?: string;
  rel?: string;
  title?: string;
  type?: string;
  length?: number;
  s3_bucket_id?: string;
  [key: string]: unknown;
}

/** `GET /collections/{collectionId}` — OGC API Features collection description. */
export interface OgcCollectionDetails {
  id?: string;
  itemType?: string;
  links?: OgcCollectionLink[];
  [key: string]: unknown;
}

/** Thrown when S3InitiateMultipartUpload returns HTTP 409 (file already exists). */
export class OgcAssetUploadConflictError extends Error {
  constructor(message = 'File already exists') {
    super(message);
    this.name = 'OgcAssetUploadConflictError';
  }
}

/** GET /jobs/{jobId} — async process job status. */
export interface OgcJobStatus {
  jobId?: string;
  processId?: string;
  status?: string;
  type?: string;
  message?: string;
  progress?: number;
  [key: string]: unknown;
}

const OGC_JOB_TERMINAL_SUCCESS = new Set([
  'successful',
  'success',
  'succeeded',
  'completed',
  'complete',
  'done',
]);

const OGC_JOB_TERMINAL_FAILURE = new Set([
  'failed',
  'failure',
  'dismissed',
  'cancelled',
  'canceled',
  'error',
  'aborted',
]);

@Injectable({
  providedIn: 'root',
})
export class OgcApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(OGC_API_BASE_URL) private readonly ogcBaseUrl: string,
    @Inject(OGC_EXECUTION_CONFIG)
    private readonly executionConfig: OgcExecutionConfig,
  ) {}

  private normalizeBase(): string {
    const base = (this.ogcBaseUrl ?? '').trim();
    if (!base) {
      throw new Error(
        'OGC API base URL is not configured (OGC_API_BASE_URL / api_ogc_server_domain).',
      );
    }
    return base.endsWith('/') ? base : `${base}/`;
  }

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
  }

  private processesUrl(): string {
    return `${this.normalizeBase()}processes`;
  }

  private processExecutionUrl(processId: string): string {
    return `${this.normalizeBase()}processes/${encodeURIComponent(processId)}/execution`;
  }

  private jobUrl(jobId: string): string {
    return `${this.normalizeBase()}jobs/${encodeURIComponent(jobId)}`;
  }

  private stacCollectionsUrl(): string {
    return `${this.normalizeBase()}stac/collections`;
  }

  private stacCollectionItemsUrl(collectionId: string): string {
    return `${this.stacCollectionsUrl()}/${encodeURIComponent(collectionId)}/items`;
  }

  private stacCollectionItemUrl(collectionId: string, itemId: string): string {
    return `${this.stacCollectionItemsUrl(collectionId)}/${encodeURIComponent(itemId)}`;
  }

  private configuredS3BucketsUrl(): string {
    return `${this.normalizeBase()}listConfiguredS3Buckets`;
  }

  private collectionUrl(collectionId: string): string {
    return `${this.normalizeBase()}collections/${encodeURIComponent(collectionId)}`;
  }

  /** GET /processes — list available OGC API Processes. */
  async getProcesses(limit = 20): Promise<OgcProcessesListResponse> {
    const url = `${this.processesUrl()}/?limit=${limit}`;
    const response = await firstValueFrom(
      this.http.get<unknown>(url, { headers: this.jsonHeaders() }),
    );
    return this.unwrapProcessesList(response);
  }

  /** GET /processes/{processId} — process description. */
  async getProcess(processId: string): Promise<Record<string, unknown>> {
    const url = `${this.normalizeBase()}processes/${encodeURIComponent(processId)}`;
    const response = await firstValueFrom(
      this.http.get<unknown>(url, { headers: this.jsonHeaders() }),
    );
    return this.unwrapRecord(response);
  }

  /** POST /stac/collections — create a STAC collection for an OGC raster dataset. */
  async createStacCollection(
    payload: StacCollectionPayload,
  ): Promise<StacCollectionResponse> {
    const response = await firstValueFrom(
      this.http.post<unknown>(this.stacCollectionsUrl(), payload, {
        headers: this.jsonHeaders(),
      }),
    );
    return this.unwrapRecord(response);
  }

  /** PUT /stac/collections/{collectionId} — update an existing STAC collection. */
  async updateStacCollection(
    collectionId: string,
    payload: StacCollectionPayload,
  ): Promise<StacCollectionResponse> {
    const response = await firstValueFrom(
      this.http.put<unknown>(
        `${this.stacCollectionsUrl()}/${encodeURIComponent(collectionId)}`,
        payload,
        { headers: this.jsonHeaders() },
      ),
    );
    return this.unwrapRecord(response);
  }

  /** GET /stac/collections/{collectionId} — fetch STAC collection; returns null when not found. */
  async getStacCollection(collectionId: string): Promise<StacCollectionResponse | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(
          `${this.stacCollectionsUrl()}/${encodeURIComponent(collectionId)}`,
          { headers: this.jsonHeaders() },
        ),
      );
      return this.unwrapRecord(response);
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 404 || error.status === 405)) {
        return null;
      }
      throw error;
    }
  }

  /**
   * GET /collections/{collectionId} — OGC API Features collection description.
   * Used to detect whether a GeoPackage asset was already onboarded (`links[].rel === 'enclosure'`).
   * Returns null when the collection has no description yet (404/405).
   */
  async getCollection(collectionId: string): Promise<OgcCollectionDetails | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<OgcCollectionDetails>(this.collectionUrl(collectionId), {
          headers: this.jsonHeaders(),
        }),
      );
      return response ?? null;
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 404 || error.status === 405)) {
        return null;
      }
      throw error;
    }
  }

  /** GET /stac/collections/{collectionId}/items — list STAC items. */
  async listStacItems(collectionId: string): Promise<StacItemCollectionResponse> {
    const response = await firstValueFrom(
      this.http.get<unknown>(this.stacCollectionItemsUrl(collectionId), {
        headers: this.jsonHeaders(),
      }),
    );
    return this.unwrapRecord(response) as StacItemCollectionResponse;
  }

  /** GET /stac/collections/{collectionId}/items/{itemId} — item details. */
  async getStacItem(
    collectionId: string,
    itemId: string,
  ): Promise<StacItemPayload> {
    const response = await firstValueFrom(
      this.http.get<unknown>(this.stacCollectionItemUrl(collectionId, itemId), {
        headers: this.jsonHeaders(),
      }),
    );
    return this.unwrapRecord(response) as unknown as StacItemPayload;
  }

  /** POST /stac/collections/{collectionId}/items — create STAC item. */
  async createStacItem(
    collectionId: string,
    payload: StacItemPayload,
  ): Promise<StacItemPayload> {
    const response = await firstValueFrom(
      this.http.post<unknown>(this.stacCollectionItemsUrl(collectionId), payload, {
        headers: this.jsonHeaders(),
      }),
    );
    return this.unwrapRecord(response) as unknown as StacItemPayload;
  }

  /** PUT /stac/collections/{collectionId}/items/{itemId} — update full STAC item. */
  async updateStacItem(
    collectionId: string,
    itemId: string,
    payload: StacItemPayload,
  ): Promise<StacItemPayload> {
    const response = await firstValueFrom(
      this.http.put<unknown>(this.stacCollectionItemUrl(collectionId, itemId), payload, {
        headers: this.jsonHeaders(),
      }),
    );
    return this.unwrapRecord(response) as unknown as StacItemPayload;
  }

  /** PATCH /stac/collections/{collectionId}/items/{itemId} — partial item update. */
  async patchStacItem(
    collectionId: string,
    itemId: string,
    payload: Partial<StacItemPayload> & Pick<StacItemPayload, 'id' | 'type'>,
  ): Promise<StacItemPayload> {
    const response = await firstValueFrom(
      this.http.patch<unknown>(this.stacCollectionItemUrl(collectionId, itemId), payload, {
        headers: this.jsonHeaders(),
      }),
    );
    return this.unwrapRecord(response) as unknown as StacItemPayload;
  }

  /** Download a file from a presigned asset URL. */
  async downloadFromUrl(url: string): Promise<Blob> {
    return firstValueFrom(
      this.http.get(url, {
        responseType: 'blob',
      }),
    );
  }

  /** GET /listConfiguredS3Buckets — buckets available for STAC asset upload. */
  async listConfiguredS3Buckets(): Promise<OgcConfiguredS3Bucket[]> {
    const response = await firstValueFrom(
      this.http.get<unknown>(this.configuredS3BucketsUrl(), {
        headers: this.jsonHeaders(),
      }),
    );
    if (Array.isArray(response)) {
      return response as OgcConfiguredS3Bucket[];
    }
    const record = this.unwrapRecord(response);
    const buckets = record['results'] ?? record['buckets'] ?? record['data'];
    return Array.isArray(buckets) ? (buckets as OgcConfiguredS3Bucket[]) : [];
  }

  /**
   * POST /processes/{processId}/execution — synchronous process execution.
   * Sends inputs in OGC execute-request shape.
   */
  async executeProcessSync(
    processId: string,
    inputs: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const body = { inputs };
    try {
      const response = await firstValueFrom(
        this.http.post<unknown>(this.processExecutionUrl(processId), body, {
          headers: this.jsonHeaders(),
        }),
      );
      return this.unwrapRecord(response);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        throw new OgcAssetUploadConflictError();
      }
      throw error;
    }
  }

  /**
   * Resolve a process id from GET /processes (matches `title` or `id`, case-insensitive).
   * e.g. CollectionOnboarding → POST …/processes/{id}/execution
   */
  findProcessIdByTitle(
    processes: OgcProcessSummary[],
    titleOrId: string,
  ): string | null {
    const needle = titleOrId.trim().toLowerCase();
    if (!needle) {
      return null;
    }
    const match = processes.find((p) => {
      const t = p.title?.trim().toLowerCase() ?? '';
      const id = p.id?.trim().toLowerCase() ?? '';
      return t === needle || id === needle;
    });
    return match?.id?.trim() ?? null;
  }

  /** Base inputs for POST /processes/{id}/execution (S3 pre-signed URL, etc.). */
  private baseExecutionInputs(
    resourceId: string,
    s3BucketIdentifier?: string,
  ): Record<string, string> {
    const cfg = this.executionConfig;
    return {
      resourceId: resourceId.trim(),
      bucketName: (cfg.bucketName ?? '').trim(),
      region: (cfg.region ?? '').trim(),
      fileType: (cfg.fileType ?? 'GeoPackage').trim(),
      version: (cfg.version ?? '1.0.0').trim(),
      s3BucketIdentifier: (
        s3BucketIdentifier?.trim() ||
        cfg.s3BucketIdentifier ||
        'default'
      ).trim(),
    };
  }

  /** Request a pre-signed URL for a single-part GPKG upload. */
  async generatePreSignedUrl(params: {
    processId: string;
    resourceId: string;
    s3BucketIdentifier?: string;
  }): Promise<OgcPresignedUrlOutputs> {
    const inputs = this.baseExecutionInputs(
      params.resourceId,
      params.s3BucketIdentifier,
    );
    const result = await this.executeProcessSync(params.processId, inputs);
    return this.extractOutputs<OgcPresignedUrlOutputs>(result);
  }

  /** Initiate multipart upload for large GeoPackage files. */
  async initiateMultipartUpload(params: {
    processId: string;  
    resourceId: string;
    fileName: string;
    fileSize: number;
    collectionId: string;
    s3BucketIdentifier?: string;
  }): Promise<OgcInitiateMultipartOutputs> {
    const inputs = {
      ...this.baseExecutionInputs(params.resourceId, params.s3BucketIdentifier),
      fileName: params.fileName,
      fileSize: params.fileSize,
      collectionId: params.collectionId,
    };
    const result = await this.executeProcessSync(params.processId, inputs);
    return this.extractOutputs<OgcInitiateMultipartOutputs>(result);
  }

  /** Complete multipart upload after all parts are uploaded to S3. */
  async completeMultipartUpload(params: {
    processId: string;
    resourceId: string;
    uploadId: string;
    filePath: string;
    parts: string[];
    s3BucketIdentifier?: string;
  }): Promise<OgcCompleteMultipartOutputs> {
    const inputs = {
      ...this.baseExecutionInputs(params.resourceId, params.s3BucketIdentifier),
      uploadId: params.uploadId,
      filePath: params.filePath,
      parts: params.parts,
    };
    const result = await this.executeProcessSync(params.processId, inputs);
    return this.extractOutputs<OgcCompleteMultipartOutputs>(result);
  }

  /** Initiate multipart upload for a STAC asset file. */
  async initiateStacAssetMultipartUpload(params: {
    processId: string;
    collectionId: string;
    itemId: string;
    bucketName: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    s3BucketIdentifier: string;
  }): Promise<OgcInitiateMultipartOutputs> {
    const inputs = {
      collectionId: params.collectionId,
      itemId: params.itemId,
      bucketName: params.bucketName,
      fileSize: params.fileSize,
      fileName: params.fileName,
      fileType: params.fileType,
      s3BucketIdentifier: params.s3BucketIdentifier,
    };
    const result = await this.executeProcessSync(params.processId, inputs);
    return this.extractOutputs<OgcInitiateMultipartOutputs>(result);
  }

  /** Complete multipart upload for a STAC asset file. */
  async completeStacAssetMultipartUpload(params: {
    processId: string;
    uploadId: string;
    bucketName: string;
    filePath: string;
    parts: string[];
    s3BucketIdentifier: string;
  }): Promise<OgcCompleteMultipartOutputs> {
    const inputs = {
      uploadId: params.uploadId,
      bucketName: params.bucketName,
      filePath: params.filePath,
      parts: params.parts,
      s3BucketIdentifier: params.s3BucketIdentifier,
    };
    const result = await this.executeProcessSync(params.processId, inputs);
    return this.extractOutputs<OgcCompleteMultipartOutputs>(result);
  }

  /** GET {ogcBase}/jobs/{jobId} — poll async process job status. */
  async getJob(jobId: string): Promise<OgcJobStatus> {
    const id = jobId?.trim();
    if (!id) {
      throw new Error('OGC job id is required.');
    }
    const response = await firstValueFrom(
      this.http.get<unknown>(this.jobUrl(id), { headers: this.jsonHeaders() }),
    );
    return this.parseJobStatus(this.unwrapRecord(response));
  }

  /**
   * Poll GET /jobs/{jobId} until the job reaches a terminal status.
   * CollectionOnboarding execution returns jobId with status ACCEPTED.
   */
  async waitForJobUntilSettled(
    jobId: string,
    options?: { intervalMs?: number; maxAttempts?: number },
  ): Promise<OgcJobStatus> {
    const intervalMs = options?.intervalMs ?? 2000;
    const maxAttempts = options?.maxAttempts ?? 120;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const job = await this.getJob(jobId);
      const normalized = this.normalizeJobStatus(job.status);

      if (OGC_JOB_TERMINAL_SUCCESS.has(normalized)) {
        return job;
      }
      if (OGC_JOB_TERMINAL_FAILURE.has(normalized)) {
        const detail =
          typeof job.message === 'string' && job.message.trim()
            ? job.message.trim()
            : `OGC job failed (${job.status ?? 'unknown'}).`;
        throw new Error(detail);
      }

      if (attempt < maxAttempts - 1) {
        await this.delay(intervalMs);
      }
    }

    throw new Error('OGC job timed out while waiting for completion.');
  }

  /**
   * POST {ogcBase}/processes/{processId}/execution — CollectionOnboarding after S3 upload.
   * Returns jobId (status ACCEPTED); then poll GET /jobs/{jobId}.
   */
  async startCollectionOnboarding(params: {
    processId: string;
    title: string;
    fileName: string;
    resourceId: string;
    description?: string;
    version?: string;
    s3BucketIdentifier?: string;
  }): Promise<OgcCollectionOnboardingOutputs> {
    const inputs = {
      ...this.baseExecutionInputs(params.resourceId, params.s3BucketIdentifier),
      title: params.title,
      fileName: params.fileName,
      description: params.description ?? '',
      ...(params.version ? { version: params.version.trim() } : {}),
    };
    const result = await this.executeProcessSync(params.processId, inputs);
    return this.parseCollectionOnboardingResponse(result);
  }

  /**
   * POST execution then poll GET /jobs/{jobId} until the onboarding job completes.
   */
  async runCollectionOnboardingAndWaitForJob(params: {
    processId: string;
    title: string;
    fileName: string;
    resourceId: string;
    description?: string;
    version?: string;
    s3BucketIdentifier?: string;
  }): Promise<OgcJobStatus> {
    const started = await this.startCollectionOnboarding(params);
    const jobId = started.jobId?.trim();
    if (!jobId) {
      throw new Error(
        'Collection onboarding did not return a job ID. Cannot track job status.',
      );
    }
    return this.waitForJobUntilSettled(jobId);
  }

  /**
   * PUT file bytes to an S3 pre-signed URL (no auth headers).
   */
  async uploadToPresignedUrl(
    presignedUrl: string,
    blob: Blob,
    onProgress?: (percent: number) => void,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http
        .put(presignedUrl, blob, {
          headers: new HttpHeaders({
            'Content-Type': blob.type || 'application/octet-stream',
          }),
          observe: 'events',
          reportProgress: true,
          responseType: 'text',
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              const pct = Math.round((event.loaded / event.total) * 100);
              onProgress?.(pct);
            } else if (event.type === HttpEventType.Response) {
              const etag = event.headers.get('ETag')?.replace(/"/g, '') ?? '';
              if (!etag) {
                reject(new Error('No ETag received from S3 upload'));
                return;
              }
              resolve(etag);
            }
          },
          error: (err) => reject(err),
        });
    });
  }

  private unwrapProcessesList(response: unknown): OgcProcessesListResponse {
    const record = this.unwrapRecord(response);
    const processes = Array.isArray(record['processes'])
      ? (record['processes'] as OgcProcessSummary[])
      : [];
    return { processes, links: record['links'] as OgcProcessLink[] | undefined };
  }

  private unwrapRecord(response: unknown): Record<string, unknown> {
    if (!response || typeof response !== 'object') return {};
    const root = response as Record<string, unknown>;
    if (root['result'] && typeof root['result'] === 'object') {
      return root['result'] as Record<string, unknown>;
    }
    if (root['data'] && typeof root['data'] === 'object') {
      const data = root['data'] as Record<string, unknown>;
      if (data['result'] && typeof data['result'] === 'object') {
        return data['result'] as Record<string, unknown>;
      }
      return data;
    }
    return root;
  }

  private parseCollectionOnboardingResponse(
    result: Record<string, unknown>,
  ): OgcCollectionOnboardingOutputs {
    const jobId = this.readStringField(result, ['jobId', 'job_id']);
    if (jobId) {
      return {
        jobId,
        status: this.readStringField(result, ['status']),
        processId: this.readStringField(result, ['processId', 'process_id']),
        type: this.readStringField(result, ['type']),
      };
    }
    return this.extractOutputs<OgcCollectionOnboardingOutputs>(result);
  }

  private parseJobStatus(record: Record<string, unknown>): OgcJobStatus {
    return {
      ...record,
      jobId:
        this.readStringField(record, ['jobId', 'job_id', 'id']) || undefined,
      processId: this.readStringField(record, ['processId', 'process_id']),
      status: this.readStringField(record, ['status']),
      type: this.readStringField(record, ['type']),
      message: this.readStringField(record, ['message', 'detail', 'description']),
    };
  }

  private normalizeJobStatus(status: unknown): string {
    return String(status ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
  }

  private readStringField(
    record: Record<string, unknown>,
    keys: string[],
  ): string {
    for (const key of keys) {
      const value = record[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private extractOutputs<T>(executionResponse: Record<string, unknown>): T {
    const outputs =
      executionResponse['outputs'] ??
      executionResponse['output'] ??
      executionResponse;
    if (outputs && typeof outputs === 'object') {
      const out = outputs as Record<string, unknown>;
      if (out['value'] && typeof out['value'] === 'object') {
        return out['value'] as T;
      }
      return out as T;
    }
    return executionResponse as T;
  }
}
